import type { UserProfile, TrainingPlan, ProgressLog, NutritionLog, NutritionPlan, SavedExercise, ExerciseLibraryItem } from "@/types";

const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
const EXERCISE_API_URL = import.meta.env.VITE_EXERCISE_API_URL || "https://api.jacobtechlabs.xyz/api/v1";

async function post(path: string, body: object) {
  const res = await fetch(`${BASE_URL}/api${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok)
    throw new Error(
      (await res.json().catch(() => ({}))).error || "Request failed",
    );

  return res.json();
}

async function get(path: string) {
  const res = await fetch(`${BASE_URL}/api${path}`);
  if (!res.ok)
    throw new Error(
      (await res.json().catch(() => ({}))).error || "Request failed",
    );
  return res.json();
}

// Profile & Plan APIs
export const api = {
  saveProfile: (
    userId: string,
    profile: Omit<UserProfile, "userId" | "updatedAt">,
  ) => {
    return post("/profile", { userId, ...profile });
  },

  generatePlan: (userId: string) => {
    return post("/plan/generate", { userId });
  },

  getCurrentPlan: (userId: string): Promise<TrainingPlan & { planJson: TrainingPlan }> => {
    return get(`/plan/current?userId=${userId}`);
  },

  // Progress APIs
  logWorkout: (userId: string, log: Omit<ProgressLog, "id" | "userId" | "createdAt">) => {
    return post("/progress/log", { userId, ...log });
  },

  getProgressHistory: (userId: string): Promise<ProgressLog[]> => {
    return get(`/progress/history?userId=${userId}`);
  },

  getProgressStats: (userId: string): Promise<{
    totalWorkouts: number;
    currentStreak: number;
    longestStreak: number;
    thisWeekWorkouts: number;
  }> => {
    return get(`/progress/stats?userId=${userId}`);
  },

  // Nutrition APIs
  logNutrition: (userId: string, log: Omit<NutritionLog, "id" | "userId" | "createdAt">) => {
    return post("/nutrition/log", { userId, ...log });
  },

  getNutritionHistory: (userId: string, date?: string): Promise<NutritionLog[]> => {
    const query = date ? `?date=${date}` : "";
    return get(`/nutrition/history?userId=${userId}${query}`);
  },

  generateNutritionPlan: (userId: string) => {
    return post("/nutrition/plan/generate", { userId });
  },

  getCurrentNutritionPlan: (userId: string): Promise<NutritionPlan> => {
    return get(`/nutrition/plan/current?userId=${userId}`);
  },

  // Exercise Favorites
  saveExercise: (userId: string, exerciseId: string, exerciseName: string) => {
    return post("/exercises/favorite", { userId, exerciseId, exerciseName });
  },

  getFavoriteExercises: (userId: string): Promise<SavedExercise[]> => {
    return get(`/exercises/favorites?userId=${userId}`);
  },
};

// Exercise Library API (external)
export const exerciseApi = {
  getBodyParts: async (): Promise<string[]> => {
    const res = await fetch(`${EXERCISE_API_URL}/bodyparts`);
    const data = await res.json();
    return data.data?.map((bp: { name: string }) => bp.name) || data || [];
  },

  getExercises: async (bodyPart?: string): Promise<ExerciseLibraryItem[]> => {
    const url = bodyPart && bodyPart !== "all"
      ? `${EXERCISE_API_URL}/bodyparts/${bodyPart}/exercises`
      : `${EXERCISE_API_URL}/exercises`;
    const res = await fetch(url);
    const data = await res.json();
    return data.data || data || [];
  },

  getExerciseById: async (id: string): Promise<ExerciseLibraryItem> => {
    const res = await fetch(`${EXERCISE_API_URL}/exercises/${id}`);
    const data = await res.json();
    return data.data || data;
  },

  searchExercises: async (query: string): Promise<ExerciseLibraryItem[]> => {
    const res = await fetch(`${EXERCISE_API_URL}/exercises`);
    const data = await res.json();
    const exercises = data.data || data || [];
    const lowerQuery = query.toLowerCase();
    return exercises.filter(
      (item: ExerciseLibraryItem) =>
        item.name.toLowerCase().includes(lowerQuery) ||
        item.target?.toLowerCase().includes(lowerQuery) ||
        item.equipment?.toLowerCase().includes(lowerQuery) ||
        item.bodyPart?.toLowerCase().includes(lowerQuery),
    );
  },

  getExercisesByTarget: async (target: string): Promise<ExerciseLibraryItem[]> => {
    const res = await fetch(`${EXERCISE_API_URL}/muscles/${target}/exercises`);
    const data = await res.json();
    return data.data || data || [];
  },

  getExercisesByEquipment: async (equipment: string): Promise<ExerciseLibraryItem[]> => {
    const res = await fetch(`${EXERCISE_API_URL}/equipments/${equipment}/exercises`);
    const data = await res.json();
    return data.data || data || [];
  },
};

// YouTube API for exercise videos
const YOUTUBE_API_KEY = import.meta.env.VITE_RAPID_API_YOUTUBE_KEY;
const YOUTUBE_API_HOST = "youtube-search-and-download.p.rapidapi.com";

export const youtubeApi = {
  searchVideos: async (exerciseName: string): Promise<any[]> => {
    if (!YOUTUBE_API_KEY) return [];
    
    const res = await fetch(
      `https://${YOUTUBE_API_HOST}/search?query=${encodeURIComponent(exerciseName + " exercise")}`,
      {
        headers: {
          "X-RapidAPI-Host": YOUTUBE_API_HOST,
          "X-RapidAPI-Key": YOUTUBE_API_KEY,
        },
      }
    );
    
    if (!res.ok) return [];
    const data = await res.json();
    return data.contents || [];
  },
};
